import { useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { doc, setDoc, collection, serverTimestamp, deleteDoc, query, orderBy, getDoc, getDocs } from "firebase/firestore";
import { saveAs } from "file-saver";

function CMS() {
  const [user, userLoading] = useAuthState(auth);

  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);

  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoading, setSubscribersLoading] = useState(true);
  const [subscribersError, setSubscribersError] = useState(null);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    const fetchProfile = async () => {
      try {
        const snap = await getDoc(doc(db, `users/${user.uid}/profile`, "data"));
        setProfile(snap.exists() ? snap.data() : null);
      } catch (e) {
        setProfileError(e);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, [user]);

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        const snap = await getDocs(
          query(collection(db, "subscribers"), orderBy("subscribedAt", "desc"))
        );
        setSubscribers(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      } catch (e) {
        setSubscribersError(e);
      } finally {
        setSubscribersLoading(false);
      }
    };
    fetchSubscribers();
  }, []);
  const isAdmin = profile?.isAdmin === true;
  const [activeTab, setActiveTab] = useState("blog");
  const [blogTitle, setBlogTitle] = useState("");
  const [blogContent, setBlogContent] = useState("");
  const [homeTitle, setHomeTitle] = useState("");
  const [homeDescription, setHomeDescription] = useState("");
  const [aboutTitle, setAboutTitle] = useState("");
  const [aboutDescription, setAboutDescription] = useState("");
  const [newsletterTitle, setNewsletterTitle] = useState("");
  const [newsletterDescription, setNewsletterDescription] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Log for debugging
  console.log("CMS render, user:", user?.uid, "isAdmin:", isAdmin, "activeTab:", activeTab, "subscribers:", subscribers, "profileError:", profileError?.message, "subscribersError:", subscribersError?.message);

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!blogTitle.trim() || !blogContent.trim()) {
      setError("Please enter both a title and content for the blog post.");
      return;
    }

    try {
      const postRef = doc(collection(db, "posts"));
      const excerpt = blogContent.trim().substring(0, 100) + (blogContent.length > 100 ? "..." : "");
      await setDoc(postRef, {
        id: postRef.id,
        title: blogTitle.trim(),
        content: blogContent.trim(),
        excerpt,
        date: serverTimestamp(),
        userId: user.uid
      });
      setSuccess("Blog post created successfully!");
      setBlogTitle("");
      setBlogContent("");
    } catch (err) {
      setError("Failed to create blog post: " + err.message);
    }
  };

  const handlePageSubmit = async (e, pageId) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const title = pageId === "home" ? homeTitle : pageId === "about" ? aboutTitle : newsletterTitle;
    const description = pageId === "home" ? homeDescription : pageId === "about" ? aboutDescription : newsletterDescription;

    if (!title.trim() || !description.trim()) {
      setError(`Please enter both a title and description for the ${pageId} page.`);
      return;
    }

    try {
      await setDoc(doc(db, "pages", pageId), {
        title: title.trim(),
        description: description.trim(),
        updatedAt: serverTimestamp(),
        userId: user.uid
      });
      setSuccess(`${pageId.charAt(0).toUpperCase() + pageId.slice(1)} page updated successfully!`);
      if (pageId === "home") {
        setHomeTitle("");
        setHomeDescription("");
      } else if (pageId === "about") {
        setAboutTitle("");
        setAboutDescription("");
      } else {
        setNewsletterTitle("");
        setNewsletterDescription("");
      }
    } catch (err) {
      setError(`Failed to update ${pageId} page: ` + err.message);
    }
  };

  const handleEmailSend = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!emailSubject.trim() || !emailBody.trim()) {
      setError("Please enter both a subject and body for the email.");
      return;
    }

    try {
      // Simulate sending email to subscribers (replace with actual email service API, e.g., SendGrid)
      console.log("Sending email to subscribers:", { subject: emailSubject, body: emailBody, subscribers: subscribers.map(s => s.email) });
      setSuccess("Email sent successfully to all subscribers!");
      setEmailSubject("");
      setEmailBody("");
    } catch (err) {
      setError("Failed to send email: " + err.message);
    }
  };

  const handleDeleteSubscriber = async (subscriberId) => {
    setError("");
    setSuccess("");
    try {
      await deleteDoc(doc(db, "subscribers", subscriberId));
      setSuccess("Subscriber deleted successfully!");
    } catch (err) {
      setError("Failed to delete subscriber: " + err.message);
    }
  };

  const handleExportSubscribers = () => {
    setError("");
    setSuccess("");
    try {
      const csvContent = [
        "Email,Status,SubscribedAt",
        ...subscribers.map(s => `${s.email},${s.status},${s.subscribedAt?.toDate().toISOString()}`)
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      saveAs(blob, "subscribers.csv");
      setSuccess("Subscribers exported successfully!");
    } catch (err) {
      setError("Failed to export subscribers: " + err.message);
    }
  };

  if (userLoading || profileLoading || subscribersLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary" aria-label="Loading" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-text-light dark:text-text-dark mb-6">
        Content Management
      </h1>
      <p className="text-lg text-neutral mb-8">
        Create and manage blog posts, page content, and newsletter subscribers for the 66 Days Journal platform.
      </p>
      {!user || !isAdmin ? (
        <div className="card p-6">
          <p className="text-error text-center">
            {user ? "You do not have permission to access the CMS." : "Please log in to access the content management system."}
          </p>
        </div>
      ) : (
        <div className="card p-6 max-w-2xl mx-auto">
          <div className="flex gap-4 mb-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab("blog")}
              className={activeTab === "blog" ? "tab tab-active" : "tab tab-inactive"}
            >
              Blog Posts
            </button>
            <button
              onClick={() => setActiveTab("home")}
              className={activeTab === "home" ? "tab tab-active" : "tab tab-inactive"}
            >
              Home
            </button>
            <button
              onClick={() => setActiveTab("about")}
              className={activeTab === "about" ? "tab tab-active" : "tab tab-inactive"}
            >
              About
            </button>
            <button
              onClick={() => setActiveTab("newsletter")}
              className={activeTab === "newsletter" ? "tab tab-active" : "tab tab-inactive"}
            >
              Newsletter
            </button>
            <button
              onClick={() => setActiveTab("subscribers")}
              className={activeTab === "subscribers" ? "tab tab-active" : "tab tab-inactive"}
            >
              Subscribers
            </button>
          </div>
          {error && (
            <p className="text-error mb-4 text-center">{error}</p>
          )}
          {success && (
            <p className="text-primary mb-4 text-center">{success}</p>
          )}
          {activeTab === "blog" && (
            <form onSubmit={handleBlogSubmit} className="space-y-4">
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={blogTitle}
                  onChange={(e) => setBlogTitle(e.target.value)}
                  className="input w-full"
                  placeholder="Enter blog post title"
                  aria-label="Blog post title"
                />
              </div>
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  Content
                </label>
                <textarea
                  value={blogContent}
                  onChange={(e) => setBlogContent(e.target.value)}
                  className="input w-full h-40"
                  placeholder="Enter blog post content"
                  aria-label="Blog post content"
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                aria-label="Save blog post"
              >
                Save Blog Post
              </button>
            </form>
          )}
          {activeTab === "home" && (
            <form onSubmit={(e) => handlePageSubmit(e, "home")} className="space-y-4">
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  Home Title
                </label>
                <input
                  type="text"
                  value={homeTitle}
                  onChange={(e) => setHomeTitle(e.target.value)}
                  className="input w-full"
                  placeholder="Enter home page title"
                  aria-label="Home page title"
                />
              </div>
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  Home Description
                </label>
                <textarea
                  value={homeDescription}
                  onChange={(e) => setHomeDescription(e.target.value)}
                  className="input w-full h-20"
                  placeholder="Enter home page description"
                  aria-label="Home page description"
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                aria-label="Save home content"
              >
                Save Home Content
              </button>
            </form>
          )}
          {activeTab === "about" && (
            <form onSubmit={(e) => handlePageSubmit(e, "about")} className="space-y-4">
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  About Title
                </label>
                <input
                  type="text"
                  value={aboutTitle}
                  onChange={(e) => setAboutTitle(e.target.value)}
                  className="input w-full"
                  placeholder="Enter about page title"
                  aria-label="About page title"
                />
              </div>
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  About Description
                </label>
                <textarea
                  value={aboutDescription}
                  onChange={(e) => setAboutDescription(e.target.value)}
                  className="input w-full h-40"
                  placeholder="Enter about page description"
                  aria-label="About page description"
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                aria-label="Save about content"
              >
                Save About Content
              </button>
            </form>
          )}
          {activeTab === "newsletter" && (
            <form onSubmit={(e) => handlePageSubmit(e, "newsletter")} className="space-y-4">
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  Newsletter Title
                </label>
                <input
                  type="text"
                  value={newsletterTitle}
                  onChange={(e) => setNewsletterTitle(e.target.value)}
                  className="input w-full"
                  placeholder="Enter newsletter page title"
                  aria-label="Newsletter page title"
                />
              </div>
              <div>
                <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                  Newsletter Description
                </label>
                <textarea
                  value={newsletterDescription}
                  onChange={(e) => setNewsletterDescription(e.target.value)}
                  className="input w-full h-20"
                  placeholder="Enter newsletter page description"
                  aria-label="Newsletter page description"
                />
              </div>
              <button
                type="submit"
                className="btn-primary w-full"
                aria-label="Save newsletter content"
              >
                Save Newsletter Content
              </button>
            </form>
          )}
          {activeTab === "subscribers" && (
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark">
                Manage Newsletter Subscribers
              </h2>
              {subscribersError && (
                <p className="text-error text-center">Error loading subscribers: {subscribersError.message}</p>
              )}
              {subscribers.length === 0 && !subscribersError && (
                <p className="text-neutral text-center">No subscribers yet.</p>
              )}
              <div className="space-y-2">
                {subscribers.map((subscriber) => (
                  <div key={subscriber.id} className="flex justify-between items-center p-2 border-b border-gray-200 dark:border-gray-600">
                    <div>
                      <p className="text-text-light dark:text-text-dark">{subscriber.email}</p>
                      <p className="text-sm text-neutral">
                        Status: {subscriber.status}, Subscribed: {subscriber.subscribedAt?.toDate().toISOString().split('T')[0]}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDeleteSubscriber(subscriber.id)}
                      className="btn-secondary text-sm px-3 py-1"
                      aria-label={`Delete subscriber ${subscriber.email}`}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
              <button
                onClick={handleExportSubscribers}
                className="btn-primary w-full mt-4"
                aria-label="Export subscribers as CSV"
              >
                Export Subscribers as CSV
              </button>
              <form onSubmit={handleEmailSend} className="space-y-4 mt-6">
                <div>
                  <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="input w-full"
                    placeholder="Enter email subject"
                    aria-label="Email subject"
                  />
                </div>
                <div>
                  <label className="block text-text-light dark:text-text-dark font-medium mb-1">
                    Email Body
                  </label>
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    className="input w-full h-40"
                    placeholder="Enter email content"
                    aria-label="Email body"
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary w-full"
                  aria-label="Send email to subscribers"
                >
                  Send Email to Subscribers
                </button>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CMS;